import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
    Card, CardContent, TextField, Autocomplete,
    Button, Box, Divider, Typography, Alert,
    Link as MuiLink,
} from "@mui/material";
import { Link } from "react-router-dom";
import api from "../../shared/api";

const createSchema = z.object({
    client_id: z.string().min(1, "The username field is required.").max(25),
    email: z.string().min(1, "The email field is required.").email("The email field must be a valid email."),
    arcID: z.number({ required_error: "The program year field is required." }),
});

const verifySchema = z.object({
    code: z.string().min(1, "The verification code field is required."),
});

export default function ForgotPasswordForm({ viewModel }) {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState("");

    const createForm = useForm({
        resolver: zodResolver(createSchema),
        defaultValues: { client_id: "", email: "", arcID: null },
    });

    const verifyForm = useForm({
        resolver: zodResolver(verifySchema),
        defaultValues: { code: "" },
    });

    useEffect(() => {
        if (viewModel.programYears.length > 0) {
            createForm.setValue("arcID", viewModel.programYears[0].arcID);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onCreate = async (data) => {
        setFormError("");
        setLoading(true);
        try {
            const response = await api.post("/forgot-password", data, { responseType: "json" });
            if (response.data.location) {
                window.location.replace(response.data.location);
                return;
            }
            if (!response.data.valid && response.data.errors) {
                response.data.errors.forEach((err) => {
                    if (err.PROPERTY.length === 0) {
                        setFormError((prev) => prev + " " + err.MESSAGE);
                    } else {
                        createForm.setError(err.PROPERTY, { message: err.MESSAGE });
                    }
                });
            } else {
                setStep(2);
            }
        } catch {
            setFormError("There was a network error processing this request. Please contact support if this problem persists.");
        } finally {
            setLoading(false);
        }
    };

    const onVerify = async (data) => {
        setFormError("");
        setLoading(true);
        try {
            const response = await api.put("/forgot-password", data, { responseType: "json" });
            if (response.data.location) {
                window.location.replace(response.data.location);
                return;
            }
            if (!response.data.valid && response.data.errors) {
                let errorMsg = "";
                response.data.errors.forEach((err) => {
                    if (err.PROPERTY.length === 0) {
                        errorMsg += " " + err.MESSAGE;
                    } else {
                        verifyForm.setError(err.PROPERTY, { message: err.MESSAGE });
                    }
                });
                setFormError(errorMsg);
                setStep(3);
            }
        } catch {
            setFormError("There was a network error processing this request. Please contact support if this problem persists.");
        } finally {
            setLoading(false);
        }
    };

    const restart = () => {
        createForm.reset({ client_id: "", email: "", arcID: viewModel.programYears[0]?.arcID ?? null });
        verifyForm.reset({ code: "" });
        setFormError("");
        setStep(1);
    };

    return (
        <Card id="forgotPassword" variant="elevation" elevation={0} square sx={{ border: "none" }}>
            <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h5">Forgot Password</Typography>
            </CardContent>

            {step === 1 && (
                <form onSubmit={createForm.handleSubmit(onCreate)}>
                    <CardContent>
                        <Box sx={{ mx: "auto", maxWidth: "83%" }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Please enter your username and primary email.
                            </Typography>

                            <Controller
                                name="client_id"
                                control={createForm.control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Username"
                                        autoFocus
                                        fullWidth
                                        inputProps={{ maxLength: 25 }}
                                        error={!!createForm.formState.errors.client_id}
                                        helperText={createForm.formState.errors.client_id?.message}
                                        sx={{ mb: 3 }}
                                    />
                                )}
                            />

                            <Controller
                                name="email"
                                control={createForm.control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Email"
                                        fullWidth
                                        error={!!createForm.formState.errors.email}
                                        helperText={createForm.formState.errors.email?.message}
                                        sx={{ mb: 3 }}
                                        onKeyDown={(e) => e.key === "Enter" && createForm.handleSubmit(onCreate)()}
                                    />
                                )}
                            />

                            {viewModel.programYears.length > 1 && (
                                <Controller
                                    name="arcID"
                                    control={createForm.control}
                                    render={({ field }) => (
                                        <Autocomplete
                                            options={viewModel.programYears}
                                            getOptionLabel={(opt) => String(opt.arcYear || "")}
                                            value={viewModel.programYears.find((y) => y.arcID === field.value) || null}
                                            onChange={(_, newVal) => field.onChange(newVal ? newVal.arcID : null)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Program Year"
                                                    error={!!createForm.formState.errors.arcID}
                                                    helperText={createForm.formState.errors.arcID?.message}
                                                    sx={{ mb: 3 }}
                                                />
                                            )}
                                            disableClearable
                                        />
                                    )}
                                />
                            )}
                        </Box>

                        <Box sx={{ mx: "auto", maxWidth: "83%", pt: 2 }}>
                            {formError ? (
                                <Typography variant="subtitle1" color="error">{formError}</Typography>
                            ) : (
                                <Typography variant="subtitle1">&nbsp;</Typography>
                            )}
                        </Box>

                        <Box sx={{ mx: "auto", maxWidth: "83%", pt: 2 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                size="large"
                                disabled={loading}
                                sx={{ bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" }, py: 1.2 }}
                            >
                                {loading ? "Submitting..." : "Next"}
                            </Button>
                        </Box>
                    </CardContent>
                </form>
            )}

            {step === 2 && (
                <form onSubmit={verifyForm.handleSubmit(onVerify)}>
                    <CardContent>
                        <Box sx={{ mx: "auto", maxWidth: "83%" }}>
                            <Alert severity="info" variant="outlined" sx={{ mb: 3 }}>
                                An email containing a verification code was sent to the email address provided.
                                <br /><strong>Enter your verification code to continue.</strong>
                            </Alert>

                            <Controller
                                name="code"
                                control={verifyForm.control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Verification Code"
                                        autoFocus
                                        fullWidth
                                        error={!!verifyForm.formState.errors.code}
                                        helperText={verifyForm.formState.errors.code?.message}
                                        sx={{ mb: 3 }}
                                        onKeyDown={(e) => e.key === "Enter" && verifyForm.handleSubmit(onVerify)()}
                                    />
                                )}
                            />
                        </Box>

                        <Box sx={{ mx: "auto", maxWidth: "83%", pt: 2 }}>
                            {formError ? (
                                <Typography variant="subtitle1" color="error">{formError}</Typography>
                            ) : (
                                <Typography variant="subtitle1">&nbsp;</Typography>
                            )}
                        </Box>

                        <Box sx={{ mx: "auto", maxWidth: "83%", pt: 2 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                size="large"
                                disabled={loading}
                                sx={{ bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" }, py: 1.2 }}
                            >
                                {loading ? "Verifying..." : "Next"}
                            </Button>
                        </Box>
                    </CardContent>
                </form>
            )}

            {step === 3 && (
                <CardContent>
                    <Alert severity="error" variant="outlined">
                        <span dangerouslySetInnerHTML={{ __html: formError }} />
                        {" "}You must start a new{" "}
                        <MuiLink href="#" onClick={(e) => { e.preventDefault(); restart(); }}>
                            forgot my password
                        </MuiLink>{" "}
                        request. All verification codes previously sent to you have been invalidated.
                    </Alert>
                </CardContent>
            )}

            <Divider />
            <CardContent>
                <Box sx={{ mx: "auto", maxWidth: "83%" }}>
                    <MuiLink component={Link} to="/login/forgot-username" display="block">Forgot my username</MuiLink>
                    <MuiLink component={Link} to="/login" display="block">Back to login</MuiLink>
                </Box>
            </CardContent>
        </Card>
    );
}
